/**
 * Blocks the retired web build and sends the nurse to the app store.
 *
 * Rendered INSTEAD of the app tree, not over it (see app/_layout.tsx). The
 * app-lock gate overlays because it must preserve in-progress visit state
 * underneath; this one has no such duty, and returning early keeps
 * AuthProvider, the WebSocket, and the push registration from ever mounting.
 * A blocked browser therefore holds no session and opens no socket.
 *
 * Staff bypass: tap the wordmark 5x. Same gesture as the login screen's hidden
 * staff password, so there is one thing to remember. See lib/web-retired.ts for
 * the persisted key and the Playwright recipe.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Linking, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { colors, fonts, radius, spacing } from "@/lib/theme";
import {
    APP_STORE_URL,
    BYPASS_TAP_THRESHOLD,
    BYPASS_TAP_WINDOW_MS,
    PLAY_STORE_URL,
    detectStoreUrl,
    storeBypass
} from "@/lib/web-retired";

function PinwheelLogo({ size = 56 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

export function WebRetiredScreen({ onBypass }: { onBypass: () => void }) {
    const tapCountRef = useRef(0);
    const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Detected once on mount: the UA cannot change under a live page, and
    // reading it during render would make the screen non-deterministic.
    const [storeUrl] = useState(() => detectStoreUrl());

    useEffect(() => {
        return () => {
            if (tapTimerRef.current) {
                clearTimeout(tapTimerRef.current);
            }
        };
    }, []);

    const handleTap = useCallback(() => {
        tapCountRef.current += 1;
        if (tapTimerRef.current) {
            clearTimeout(tapTimerRef.current);
        }
        if (tapCountRef.current >= BYPASS_TAP_THRESHOLD) {
            tapCountRef.current = 0;
            storeBypass();
            onBypass();
            return;
        }
        tapTimerRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, BYPASS_TAP_WINDOW_MS);
    }, [onBypass]);

    const open = useCallback((url: string) => {
        void Linking.openURL(url);
    }, []);

    return (
        <SafeAreaView style={styles.safe}>
            <View style={styles.wrap}>
                {/* The bypass target. `activeOpacity={1}` and no affordance:
                    it has to read as the logo, not as a button. */}
                <TouchableOpacity
                    activeOpacity={1}
                    onPress={handleTap}
                    style={styles.logoArea}
                    accessibilityRole="image"
                    accessibilityLabel="Task Health"
                >
                    <PinwheelLogo size={56} />
                    <Text style={styles.brand}>TaskHealth</Text>
                </TouchableOpacity>

                <Text style={styles.title}>The web version has moved</Text>
                <Text style={styles.body}>
                    Task Health now runs in the app on your phone. The browser version is no longer
                    active.
                </Text>
                <Text style={styles.body}>
                    Download the app and sign in with the same mobile number. Everything on your
                    account is already there.
                </Text>

                {/*  One button when we know the device, both when we do not.
                    A wrong single button is worse than a choice: it sends an
                    Android nurse to a listing she cannot install. */}
                {storeUrl ? (
                    <TouchableOpacity style={styles.button} onPress={() => open(storeUrl)}>
                        <Text style={styles.buttonText}>Download the app</Text>
                    </TouchableOpacity>
                ) : (
                    <View style={styles.buttonStack}>
                        <TouchableOpacity style={styles.button} onPress={() => open(APP_STORE_URL)}>
                            <Text style={styles.buttonText}>Download for iPhone</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={styles.buttonSecondary}
                            onPress={() => open(PLAY_STORE_URL)}
                        >
                            <Text style={styles.buttonSecondaryText}>Download for Android</Text>
                        </TouchableOpacity>
                    </View>
                )}

                <Text style={styles.help}>
                    Need help? Text us at (929) 702-1946 and we will get you set up.
                </Text>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.gray50 },
    wrap: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: 28,
        maxWidth: 460,
        width: "100%",
        alignSelf: "center",
        gap: spacing.base
    },
    logoArea: { alignItems: "center", gap: spacing.md, marginBottom: spacing.sm },
    brand: {
        fontSize: 20,
        fontFamily: fonts.extraBold,
        color: colors.ink,
        letterSpacing: -0.4
    },
    title: {
        fontSize: 26,
        fontFamily: fonts.extraBold,
        color: colors.gray900,
        textAlign: "center"
    },
    body: {
        fontSize: 15,
        fontFamily: fonts.regular,
        color: colors.gray600,
        lineHeight: 22,
        textAlign: "center"
    },
    buttonStack: { width: "100%", gap: spacing.md, marginTop: spacing.sm },
    button: {
        marginTop: spacing.sm,
        backgroundColor: colors.teal400,
        borderRadius: radius.lg,
        paddingVertical: 18,
        paddingHorizontal: spacing.xl,
        alignItems: "center",
        alignSelf: "stretch"
    },
    buttonText: { fontSize: 16, fontFamily: fonts.bold, color: colors.ink },
    buttonSecondary: {
        borderRadius: radius.lg,
        paddingVertical: 18,
        paddingHorizontal: spacing.xl,
        alignItems: "center",
        alignSelf: "stretch",
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    buttonSecondaryText: { fontSize: 16, fontFamily: fonts.bold, color: colors.gray900 },
    help: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.gray600,
        textAlign: "center",
        marginTop: spacing.base
    }
});
